import { BinaryFrame, FrameType } from '../transport/protocol';
import { VideoRenderer } from './types';
import { WasmH264Renderer } from './wasm';
import { UnsupportedVideoRenderer } from './unsupported';

export class WebCodecsRenderer implements VideoRenderer {
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private decoder: VideoDecoder | null = null;
  private fallbackRenderer: VideoRenderer;
  private isWebCodecsActive = false;
  private frameCount = 0;
  private lastFpsCalc = Date.now();
  private currentFps = 0;
  private hasKeyframe = false;
  private configuredCodec: string | null = null;

  constructor(
    private readonly onKeyframeNeeded: () => void = () => {},
    wasmAvailable = typeof WebAssembly === 'object',
  ) {
    this.fallbackRenderer = wasmAvailable ? new WasmH264Renderer() : new UnsupportedVideoRenderer();
  }

  public async initialize(canvas: HTMLCanvasElement): Promise<void> {
    this.canvas = canvas;
    this.canvas.width = 1280;
    this.canvas.height = 720;
    this.ctx = canvas.getContext('2d', { alpha: false });

    try {
      await this.fallbackRenderer.initialize(canvas);
    } catch (error) {
      console.warn('[DriveDeck WebCodecs] WASM fallback unavailable:', error);
      this.fallbackRenderer.destroy();
      this.fallbackRenderer = new UnsupportedVideoRenderer();
      await this.fallbackRenderer.initialize(canvas);
    }

    // Try initializing WebCodecs VideoDecoder if supported by the browser
    if (typeof window.VideoDecoder !== 'undefined') {
      try {
        this.decoder = new VideoDecoder({
          output: (videoFrame: VideoFrame) => {
            if (this.hasKeyframe && this.ctx && this.canvas) {
              this.ctx.drawImage(videoFrame, 0, 0, this.canvas.width, this.canvas.height);
              this.frameCount++;
              const now = Date.now();
              if (now - this.lastFpsCalc >= 1000) {
                this.currentFps = Math.round((this.frameCount * 1000) / (now - this.lastFpsCalc));
                this.frameCount = 0;
                this.lastFpsCalc = now;
              }
            }
            videoFrame.close();
          },
          error: (err: DOMException) => {
            console.warn('[DriveDeck WebCodecs] Decoder error, falling back to WASM H.264:', err);
            this.switchToFallback();
          },
        });

        this.isWebCodecsActive = true;
      } catch (e) {
        console.warn('[DriveDeck WebCodecs] Failed to configure VideoDecoder:', e);
        this.isWebCodecsActive = false;
      }
    }
    if (this.isWebCodecsActive) this.ctx?.clearRect(0, 0, canvas.width, canvas.height);
  }

  public renderFrame(frame: BinaryFrame): void {
    if (!this.isWebCodecsActive || !this.decoder) {
      if (frame.type === FrameType.KEYFRAME) this.hasKeyframe = true;
      if (!this.hasKeyframe) return;
      this.fallbackRenderer.renderFrame(frame);
      return;
    }

    try {
      const isKeyframe = frame.type === FrameType.KEYFRAME;
      if (isKeyframe) {
        const codec = codecFromSps(frame.payload);
        if (!codec && !this.configuredCodec) {
          this.switchToFallback();
          this.hasKeyframe = true;
          this.fallbackRenderer.renderFrame(frame);
          return;
        }
        if (codec && codec !== this.configuredCodec) {
          this.decoder.configure({ codec, optimizeForLatency: true });
          this.configuredCodec = codec;
        }
        this.hasKeyframe = true;
      }

      // Drop P-frames until the first Keyframe (IDR) is received
      if (!this.hasKeyframe) {
        return;
      }

      const chunk = new EncodedVideoChunk({
        type: isKeyframe ? 'key' : 'delta',
        timestamp: frame.timestampMs * 1000, // microseconds
        data: frame.payload,
      });

      this.decoder.decode(chunk);
    } catch (e) {
      console.warn('[DriveDeck WebCodecs] Decode error, routing to fallback:', e);
      this.switchToFallback();
      if (frame.type === FrameType.KEYFRAME) {
        this.hasKeyframe = true;
        this.fallbackRenderer.renderFrame(frame);
      }
    }
  }

  private switchToFallback(): void {
    this.isWebCodecsActive = false;
    this.hasKeyframe = false;
    this.onKeyframeNeeded();
  }

  public reset(): void {
    this.hasKeyframe = false;
    this.configuredCodec = null;
    if (this.decoder && this.decoder.state !== 'closed') this.decoder.reset();
    this.frameCount = this.currentFps = 0;
    this.lastFpsCalc = Date.now();
    this.fallbackRenderer.reset();
    if (this.canvas) this.ctx?.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  public getFps(): number {
    return this.isWebCodecsActive && this.currentFps > 0
      ? this.currentFps
      : this.fallbackRenderer.getFps();
  }

  public getName(): string {
    return this.isWebCodecsActive
      ? 'WebCodecs H.264 Hardware Decoder'
      : this.fallbackRenderer.getName();
  }

  public destroy(): void {
    if (this.decoder) {
      try {
        this.decoder.close();
      } catch (e) {
        console.warn('[DriveDeck WebCodecs] Error closing decoder:', e);
      }
      this.decoder = null;
    }
    this.fallbackRenderer.destroy();
    this.ctx = null;
    this.canvas = null;
  }
}

export function codecFromSps(data: Uint8Array): string | null {
  for (let i = 0; i + 6 < data.length; i++) {
    if (data[i] !== 0 || data[i + 1] !== 0) continue;
    const header = data[i + 2] === 1 ? i + 3 :
      data[i + 2] === 0 && data[i + 3] === 1 ? i + 4 : -1;
    if (header >= 0 && (data[header] & 31) === 7 && header + 3 < data.length) {
      return 'avc1.' + Array.from(data.subarray(header + 1, header + 4))
        .map(value => value.toString(16).padStart(2, '0')).join('').toUpperCase();
    }
  }
  return null;
}
